<template>
  <Teleport :to="portalTarget || 'body'" :disabled="disabled || !portalTarget">
    <slot></slot>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, onBeforeUnmount, computed } from 'vue'
import { canUseDOM } from '../../utils'

type Props = {
  target: string
  as?: string
  disabled?: boolean
  unmountOnDestroy?: boolean
  name?: string
}

const props = withDefaults(defineProps<Props>(), {
  as: 'div',
  disabled: false,
  unmountOnDestroy: true,
})

defineOptions({
  name: 'SbPortal',
  inheritAttrs: false,
})

const portalElement = ref<Element | null>(null)
const hasWrapperParent = ref(false)

const portalTarget = computed(() => {
  if (!canUseDOM || props.disabled) {
    return null
  }

  if (portalElement.value) {
    return portalElement.value
  }

  // Find existing element first otherwise return null
  return document.querySelector(props.target)
})

function createPortalTarget(target: string, tag: string): Element | undefined {
  if (!canUseDOM || !target) {
    return
  }

  // prevent create more than one destination element
  const existingPortalElement = document.querySelector(target)

  if (existingPortalElement) {
    return existingPortalElement
  }
  const wrapper = document.querySelector('#portal-wrapper')
  const parentEl = wrapper || document.body
  if (wrapper) hasWrapperParent.value = true

  const el = document.createElement(tag)
  if (target.startsWith('#')) {
    el.id = target.slice(target.indexOf('#') + 1)
  }

  if (parentEl) {
    parentEl.appendChild(el)
  }
  return el
}

function mountTarget(): void {
  if (!canUseDOM) {
    return
  }
  portalElement.value = createPortalTarget(props.target, props.as) || null
}

function unmountTarget(): void {
  if (
    !props.disabled &&
    props.unmountOnDestroy &&
    props.target.length > 0 &&
    canUseDOM &&
    portalElement.value
  ) {
    const parent = portalElement.value.parentNode
    if (parent) {
      parent.removeChild(portalElement.value)
    }
  }
  portalElement.value = null
}

onBeforeUnmount(() => {
  unmountTarget()
})

defineExpose({
  mountTarget,
})

if (!props.disabled) {
  mountTarget()
}
</script>
